import type { OverlayPlan } from './overlayPlan.js';

/**
 * SVG overlay renderer.
 *
 * The SVG is a sibling of the photo in the same unchanged content element, so it
 * shares the image coordinate system exactly: `viewBox="0 0 1 1"` +
 * `preserveAspectRatio="none"` maps normalized coordinates onto the photo. There
 * is no user transform. Nothing here recomputes geometry — it only materializes
 * the plan.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface RenderOverlayOptions {
  /** Stroke width in CSS pixels of the displayed (contain-fitted) photo. */
  strokeWidth: number;
  /** Content size in CSS pixels, used to keep dots round. */
  baseSize: { width: number; height: number };
  landmarkDotRadius?: number;
}

export const renderOverlay = (
  svg: SVGSVGElement,
  plan: OverlayPlan,
  options: RenderOverlayOptions,
): void => {
  svg.setAttribute('viewBox', '0 0 1 1');
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('aria-hidden', 'true');
  svg.style.setProperty('--pv-stroke', `${options.strokeWidth.toFixed(2)}px`);

  const nodes: SVGElement[] = [];

  for (const shape of plan.shapes) {
    const element = document.createElementNS(SVG_NS, shape.element);
    for (const [name, value] of Object.entries(shape.attributes)) {
      element.setAttribute(name, String(value));
    }
    if (shape.polygonPoints) {
      element.setAttribute('points', shape.polygonPoints);
    }
    element.setAttribute('class', shape.className);
    element.setAttribute('vector-effect', 'non-scaling-stroke');
    element.dataset['measurementId'] = shape.measurementId;
    const title = document.createElementNS(SVG_NS, 'title');
    title.textContent = shape.title;
    element.append(title);
    nodes.push(element);
  }

  if (plan.landmarks.length > 0) {
    const radius = options.landmarkDotRadius ?? 1.4;
    const group = document.createElementNS(SVG_NS, 'g');
    group.setAttribute('class', 'pv-landmarks');
    const rx = radius / Math.max(options.baseSize.width, 1);
    const ry = radius / Math.max(options.baseSize.height, 1);
    for (const [index, point] of plan.landmarks.entries()) {
      const dot = document.createElementNS(SVG_NS, 'ellipse');
      dot.setAttribute('cx', String(point.x));
      dot.setAttribute('cy', String(point.y));
      dot.setAttribute('rx', String(rx));
      dot.setAttribute('ry', String(ry));
      dot.setAttribute('class', 'pv-shape pv-shape--landmark pv-status-uncertain pv-shape--dim');
      dot.dataset['landmark'] = String(index);
      group.append(dot);
    }
    nodes.push(group);
  }

  svg.replaceChildren(...nodes);
};

export const clearOverlay = (svg: SVGSVGElement): void => {
  svg.replaceChildren();
};
